import React, { Suspense, lazy, useState } from "react";
import { Card, CardBody, CardHeader, Container, Col, Row } from "reactstrap";
import { List } from "@repoflow/components/list-cursor";
import * as Section from "@repoflow/components/section";
import * as Loading from "@repoflow/components/loading";
import CaptureSchema from "@common/schema/capture/public";
import * as CaptureQL from "queries/capture";

import { Filter, DEFAULT_RANGE, buildFilter, rangeLabel } from "components/statistics/filter";
import { asListingRow } from "components/statistics/format";
import { Limits } from "../limits";

// recharts and its d3 tree are ~100KB gzipped and reachable from exactly two
// screens. Lazily loaded so the bucket list — the app's landing page — never
// pays for them.
const CaptureCharts = lazy(() => import("../charts"));

export const Sidebar = () => null;

export const Indice = () => null;

// Everything about this bucket that is a number, on one screen.
//
// Two different kinds of number, and the layout keeps them apart on purpose:
//
//   Limits    ABSOLUTE. What the bucket is allowed and how much is gone,
//             aggregated over each limit's own period. Not affected by the
//             range picker — a quota does not mean anything "in the last 24
//             hours" — which is why it sits ABOVE the filter rather than under
//             it with everything the filter scopes.
//
//   The rest  DELTAS over the selected window. Every measure on the capture
//             path is a net change, so nothing below the filter is a size.
//
// Mixing the two under one range control is how a windowed delta ends up read
// as a total, which is the misreading this whole section is arranged to avoid.
export const Content = ({ match: { params } }) => {
  // The bucket is already in section context — Section.Query in
  // routes/buckets/bucket/index.js put it there — so the extension suggestions
  // and the per-file bounds come from the bucket's own row without a second
  // fetch.
  const { values } = Section.useSectionContext();

  const [applied, setApplied] = useState(() => ({
    filter: buildFilter({ range: DEFAULT_RANGE }),
    range: DEFAULT_RANGE,
  }));

  return (
    <Container>
      <div className="mt-4"></div>

      <Row>
        <Col>
          <Limits bucketId={params.bucketId} values={values || {}} />
        </Col>
      </Row>

      {/* One filter row above everything it scopes. The charts and the table
          below both re-render against this slice; neither has a filter of its
          own, and the Limits panel above is deliberately outside it. */}
      <Row>
        <Col>
          <Filter exts={values?.exts || []} onApply={setApplied} />
        </Col>
      </Row>

      <Row>
        <Col>
          <Suspense fallback={<Loading.Content />}>
            <CaptureCharts
              bucketId={params.bucketId}
              filter={applied.filter}
              range={applied.range}
              rangeLabel={rangeLabel(applied.range)}
            />
          </Suspense>
        </Col>
      </Row>

      <div className="mt-2"></div>

      <Row>
        <Col>
          <Card className="mb-3">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <strong>Activity</strong>
              <small className="text-muted">{rangeLabel(applied.range)}</small>
            </CardHeader>
            <CardBody className="pt-2">
              <List
                // Remounts the list when the filter changes. list-cursor resets
                // its accumulated pages when orderBy/orderMode change but NOT
                // when `variables` do, so without this a new filter's first
                // page would append underneath the previous filter's results
                // instead of replacing them.
                key={JSON.stringify(applied.filter)}
                path={"viewer.account.buckets.get.captures"}
                query={CaptureQL.list}
                variables={{ ...params, filter: applied.filter }}
                schema={CaptureSchema}
                // The Listing projection, not /Public/Capture: its dimensions
                // and measures are flattened into named typed columns by
                // mapItem below. Rendering the arrays directly shows every
                // value as "true" — see the note on /Public/Capture/Listing in
                // the common schema.
                target={{ $ref: "/Public/Capture/Listing" }}
                mapItem={asListingRow}
                // The server orders by time and ignores orderBy — but
                // list-cursor only draws its sort toggle on the column whose
                // name matches orderBy, so naming `timestamp` is what puts the
                // control on the right header.
                orderBy={"timestamp"}
                orderMode={"desc"}
                empty={"No captures in this range."}
                config={{
                  "/Public/Capture/Listing": {
                    fields: [
                      "timestamp",
                      "eventType",
                      "ext",
                      "size",
                      "totalSize",
                      "captureId",
                    ],
                  },
                }}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
